import React, { useRef, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from './utils';

interface ReactQuillEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const CustomEditor: React.FC<ReactQuillEditorProps> = ({ value, onChange, placeholder, className }) => {
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const editor = quillRef.current.getEditor();

    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData || !quillRef.current) return;

      // Get the items from the clipboard
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // Check if the item is an image
        if (item.type.indexOf('image') === 0) {
          // Prevent the browser's default paste handling
          event.preventDefault();

          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();

            reader.onload = (e) => {
              const imageUrl = e.target?.result as string;
              const range = editor.getSelection(true); // Get cursor position
              
              // Insert the Base64 image into the editor
              editor.insertEmbed(range.index, 'image', imageUrl, 'user');
            };

            // Read the image file as a Base64 data URL
            reader.readAsDataURL(file);
          }
          // We only handle the first image we find
          return;
        }
      }
    };

    // Add the event listener to the editor's root element
    editor.root.addEventListener('paste', handlePaste);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      editor.root.removeEventListener('paste', handlePaste);
    };
  }, []); // The empty dependency array ensures this runs only once on mount

  const imageHandler = () => {
    if (!quillRef.current) {
      return;
    }
    
    const editor = quillRef.current.getEditor();
    
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files ? input.files[0] : null;
      if (file && quillRef.current) {
        const reader = new FileReader();

        reader.onload = () => {
          const imageUrl = reader.result as string;
          const range = editor.getSelection(true);

          editor.insertEmbed(range.index, 'image', imageUrl, 'user');
        };

        reader.readAsDataURL(file);
      }
    };
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        'image': imageHandler,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  }), []);

  return (
    <div className={cn("flex flex-col border border-gray-300 rounded-md overflow-auto resize-y min-h-[300px]", className)}>
        <style>{`
            .ql-container.ql-snow {
                border: none !important;
            }
        `}</style>
        <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            placeholder={placeholder || 'Start writing...'}
            style={{ minHeight: '300px', border: 'none !important' }}
        />
    </div>
  );
};

export default CustomEditor;
